import React, { useState } from 'react';
import { FaLeaf, FaWater, FaBug, FaSeedling, FaCloudSun, FaTree, FaDownload, FaArrowUp, FaArrowDown, FaArrowRight, FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import { SUSTAINABILITY_DATA } from '../data/dashboardData';
import { useBiodiversityMetrics } from '../hooks/useRealTimeData';
import '../styles/sustainability.css';

// Get scores from central data configuration
const centralScores = SUSTAINABILITY_DATA.dimensions.reduce((acc, d) => {
  acc[d.name] = { score: d.score, trend: d.trend };
  return acc;
}, {});

const SustainabilityIndex = () => {
  const [selectedRegion, setSelectedRegion] = useState('DMV Region');
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedDimension, setExpandedDimension] = useState(null);

  // Fetch real-time context
  const { data: realTimeData } = useBiodiversityMetrics(38.9, -77.0, 50);

  // Initialize dimensions with scores from central data config
  const getScore = (name) => {
    if (name === 'Biodiversity' && realTimeData?.metrics) {
      return Math.round(realTimeData.metrics.shannon_index * 25);
    }
    if (name === 'Soil Health' && realTimeData?.habitat_assessment) {
      return realTimeData.habitat_assessment.quality_score;
    }
    return centralScores[name]?.score || 70;
  };

  const getTrend = (name) => centralScores[name]?.trend || 'stable';

  const initialDimensions = [
    {
      id: 1,
      name: 'Soil Health',
      score: getScore('Soil Health'),
      icon: <FaSeedling />,
      trend: getTrend('Soil Health'),
      description: 'Measures the quality, structure, and nutrient content of soil.',
      calculation: [
        { name: 'Organic matter content', percentage: 25 },
        { name: 'Soil structure and tilth', percentage: 20 },
        { name: 'Biological activity', percentage: 20 },
        { name: 'Nutrient balance', percentage: 20 },
        { name: 'Absence of contaminants', percentage: 15 }
      ],
      strategies: [
        'Cover cropping',
        'Reduced tillage',
        'Organic matter additions',
        'Crop rotation'
      ],
      chartData: [
        { month: 'Jan', value: 78 },
        { month: 'Feb', value: 80 },
        { month: 'Mar', value: 82 },
        { month: 'Apr', value: 83 },
        { month: 'May', value: 85 }
      ]
    },
    {
      id: 2,
      name: 'Water Management',
      score: getScore('Water Management'),
      icon: <FaWater />,
      trend: getTrend('Water Management'),
      description: 'Evaluates water use efficiency, conservation practices, and water quality protection.',
      calculation: [
        { name: 'Irrigation efficiency', percentage: 30 },
        { name: 'Runoff prevention', percentage: 25 },
        { name: 'Water quality protection', percentage: 25 },
        { name: 'Water conservation practices', percentage: 20 }
      ],
      strategies: [
        'Drip irrigation',
        'Rainwater harvesting',
        'Buffer zones near water bodies',
        'Precision irrigation scheduling'
      ],
      chartData: [
        { month: 'Jan', value: 71 },
        { month: 'Feb', value: 72 },
        { month: 'Mar', value: 73 },
        { month: 'Apr', value: 72 },
        { month: 'May', value: 72 }
      ]
    },
    {
      id: 3,
      name: 'Biodiversity',
      score: getScore('Biodiversity'),
      icon: <FaTree />,
      trend: getTrend('Biodiversity'),
      description: 'Assesses the variety of plant and animal species present in and around agricultural lands.',
      calculation: [
        { name: 'Crop diversity', percentage: 25 },
        { name: 'Beneficial insect populations', percentage: 20 },
        { name: 'Wildlife habitat', percentage: 20 },
        { name: 'Native plant preservation', percentage: 20 },
        { name: 'Pollinator support', percentage: 15 }
      ],
      strategies: [
        'Hedgerows and buffer strips',
        'Diverse crop rotations',
        'Habitat corridors',
        'Reduced pesticide use'
      ],
      chartData: [
        { month: 'Jan', value: 62 },
        { month: 'Feb', value: 64 },
        { month: 'Mar', value: 65 },
        { month: 'Apr', value: 67 },
        { month: 'May', value: 68 }
      ]
    },
    {
      id: 4,
      name: 'Pest Management',
      score: getScore('Pest Management'),
      icon: <FaBug />,
      trend: getTrend('Pest Management'),
      description: 'Evaluates approaches to managing pests while minimizing environmental impact.',
      calculation: [
        { name: 'Integrated Pest Management adoption', percentage: 35 },
        { name: 'Reduced chemical dependency', percentage: 25 },
        { name: 'Biological control utilization', percentage: 20 },
        { name: 'Pest monitoring practices', percentage: 20 }
      ],
      strategies: [
        'Beneficial insect habitat',
        'Crop rotation',
        'Resistant varieties',
        'Trap crops'
      ],
      chartData: [
        { month: 'Jan', value: 75 },
        { month: 'Feb', value: 76 },
        { month: 'Mar', value: 77 },
        { month: 'Apr', value: 76 },
        { month: 'May', value: 76 }
      ]
    },
    {
      id: 5,
      name: 'Climate Resilience',
      score: getScore('Climate Resilience'),
      icon: <FaCloudSun />,
      trend: getTrend('Climate Resilience'),
      description: 'Measures the farm\'s ability to withstand and adapt to climate change impacts.',
      calculation: [
        { name: 'Drought resistance', percentage: 25 },
        { name: 'Flood mitigation', percentage: 25 },
        { name: 'Temperature extremes adaptation', percentage: 25 },
        { name: 'Weather variability planning', percentage: 25 }
      ],
      strategies: [
        'Diversified cropping systems',
        'Water storage systems',
        'Shade structures',
        'Windbreaks'
      ],
      chartData: [
        { month: 'Jan', value: 67 },
        { month: 'Feb', value: 65 },
        { month: 'Mar', value: 64 },
        { month: 'Apr', value: 63 },
        { month: 'May', value: 62 }
      ]
    },
    {
      id: 6,
      name: 'Energy Efficiency',
      score: getScore('Energy Efficiency'),
      icon: <FaLeaf />,
      trend: getTrend('Energy Efficiency'),
      description: 'Evaluates energy use and renewable energy adoption in farming operations.',
      calculation: [
        { name: 'Fuel efficiency', percentage: 30 },
        { name: 'Electricity conservation', percentage: 25 },
        { name: 'Renewable energy use', percentage: 25 },
        { name: 'Equipment efficiency', percentage: 20 }
      ],
      strategies: [
        'Solar power adoption',
        'Efficient equipment',
        'Reduced tillage',
        'Optimized transportation'
      ],
      chartData: [
        { month: 'Jan', value: 75 },
        { month: 'Feb', value: 76 },
        { month: 'Mar', value: 77 },
        { month: 'Apr', value: 78 },
        { month: 'May', value: 79 }
      ]
    }
  ];

  // Use central data for these values
  const overallScore = SUSTAINABILITY_DATA.overallScore;
  const complianceStatus = SUSTAINABILITY_DATA.complianceStatus;
  const lastUpdated = SUSTAINABILITY_DATA.lastUpdated;

  const getScoreColor = (score) => {
    if (score >= 80) return 'var(--accent-green)';
    if (score >= 60) return 'var(--accent-yellow)';
    return 'var(--accent-red)';
  };

  const getScoreCategory = (score) => {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <FaArrowUp />;
      case 'declining': return <FaArrowDown />;
      case 'stable': return <FaArrowRight />;
      default: return null;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'improving': return 'var(--accent-green)';
      case 'declining': return 'var(--accent-red)';
      case 'stable': return 'var(--accent-yellow)';
      default: return 'var(--text-color)';
    }
  };

  const handleGenerateReport = () => {
    setIsGenerating(true);
    const doc = new jsPDF();
    doc.setFontSize(22);
    doc.text("Sustainability Compliance Report", 20, 20);
    doc.setFontSize(12);
    doc.text(`Region: ${selectedRegion}`, 20, 40);
    doc.text(`Overall RSI: ${overallScore}`, 20, 50);
    doc.text(`Status: ${complianceStatus}`, 20, 60);

    doc.text("Dimension Breakdown:", 20, 80);
    initialDimensions.forEach((d, i) => {
      doc.text(`- ${d.name}: ${d.score}/100`, 30, 90 + (i * 10));
    });

    setTimeout(() => {
      doc.save(`RSI-Report-${selectedRegion}.pdf`);
      setIsGenerating(false);
    }, 1500);
  };

  const handleExpandDimension = (id) => {
    if (expandedDimension === id) {
      setExpandedDimension(null);
    } else {
      setExpandedDimension(id);
    }
  };

  const renderExpandedContent = (dimension) => {
    return (
      <div className="dimension-expanded-content">
        <button
          className="close-expanded-button"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedDimension(null);
          }}
        >
          <FaTimes />
        </button>

        <div className="dimension-section">
          <h5>Description</h5>
          <p>{dimension.description}</p>
        </div>

        <div className="dimension-section">
          <h5>Calculation Factors</h5>
          <div className="calculation-factors">
            {dimension.calculation.map((factor, index) => (
              <div key={index} className="calculation-factor">
                <div className="factor-name">{factor.name}</div>
                <div className="factor-percentage">{factor.percentage}%</div>
                <div className="factor-bar">
                  <div
                    className="factor-bar-fill"
                    style={{ width: `${factor.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dimension-section">
          <h5>Improvement Strategies</h5>
          <ul className="strategies-list">
            {dimension.strategies.map((strategy, index) => (
              <li key={index}>{strategy}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div className="sustainability-index">
      <div className="section-header">
        <h2>{selectedRegion} Sustainability Index (RSI)</h2>
        <div className="header-actions">
          <select
            className="region-selector"
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
          >
            <option value="DMV Region">DMV Region</option>
            <option value="Maryland">Maryland</option>
            <option value="Virginia">Virginia</option>
            <option value="District of Columbia">District of Columbia</option>
          </select>
          <button
            className="report-button"
            onClick={handleGenerateReport}
            disabled={isGenerating}
          >
            <FaDownload /> {isGenerating ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      <div className="rsi-overview">
        <div className="rsi-score-card">
          <div className="rsi-score" style={{ color: getScoreColor(overallScore) }}>
            {overallScore}
          </div>
          <div className="rsi-score-label">Overall RSI Score</div>
          <div className="rsi-meta">
            <div className="rsi-status">Status: {complianceStatus}</div>
            <div className="rsi-updated">Last updated: {lastUpdated}</div>
          </div>
        </div>

        <div className="rsi-info">
          <h3>About the {selectedRegion} Sustainability Index</h3>
          <p>
            The RSI calculates ecosystem health across 12 dimensions, providing a comprehensive
            view of agricultural sustainability in the {selectedRegion}. Local governments can link subsidies
            to RSI scores and automatically generate compliance reports for SDG tracking.
          </p>
        </div>
      </div>

      <div className="rsi-dimensions">
        <h3>Sustainability Dimensions</h3>
        <div className="dimensions-grid">
          {initialDimensions.map(dimension => (
            <div
              key={dimension.id}
              className={`dimension-card ${expandedDimension === dimension.id ? 'expanded' : ''}`}
              data-score={getScoreCategory(dimension.score)}
              onClick={() => handleExpandDimension(dimension.id)}
            >
              <div className="dimension-header">
                <div className="dimension-icon">{dimension.icon}</div>
                <div className="dimension-details">
                  <h4>{dimension.name}</h4>
                  <div
                    className="dimension-score"
                    style={{ color: getScoreColor(dimension.score) }}
                  >
                    {dimension.score}
                  </div>
                  <div
                    className="dimension-trend"
                    style={{ color: getTrendColor(dimension.trend) }}
                  >
                    {dimension.trend} {getTrendIcon(dimension.trend)}
                  </div>
                </div>
                <button className="expand-button">
                  {expandedDimension === dimension.id ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>

              {expandedDimension === dimension.id && renderExpandedContent(dimension)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SustainabilityIndex;